import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Star, Trophy } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const TournamentCard = ({ tournament, featured = false }) => {
  const { isDarkMode } = useTheme();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Mens_Open':
        return 'bg-fsgu-blue-100 text-fsgu-blue-700 dark:bg-fsgu-blue-900/30 dark:text-fsgu-blue-300';
      case 'Ladies_Open':
        return 'bg-fsgu-gold-100 text-fsgu-gold-700 dark:bg-fsgu-gold-900/30 dark:text-fsgu-gold-300';
      case 'Junior_Boys':
      case 'Junior_Girls':
        return 'bg-fsgu-green-100 text-fsgu-green-700 dark:bg-fsgu-green-900/30 dark:text-fsgu-green-300';
      case 'Mens_Mid_Am':
        return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
      case 'Ladies_Mid_Am':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'Mens_Senior':
      case 'Ladies_Senior':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Mens_Super_Senior':
      case 'Ladies_Super_Senior':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'Country_Districts':
        return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
      case 'Mixed':
        return 'bg-fsgu-silver-200 text-fsgu-silver-700 dark:bg-fsgu-gray-700 dark:text-fsgu-silver-300';
      default:
        return 'bg-fsgu-silver-200 text-fsgu-silver-700 dark:bg-fsgu-gray-700 dark:text-fsgu-silver-300';
    }
  };

  const formatCategoryLabel = (category) => {
    const labels = {
      'Mens_Open': "Men's Open",
      'Mens_Mid_Am': "Men's Mid-Am",
      'Mens_Senior': "Men's Senior",
      'Mens_Super_Senior': "Men's Super Senior",
      'Ladies_Open': "Ladies Open",
      'Ladies_Mid_Am': "Ladies Mid-Am",
      'Ladies_Senior': "Ladies Senior",
      'Ladies_Super_Senior': "Ladies Super Senior",
      'Junior_Boys': "Junior Boys",
      'Junior_Girls': "Junior Girls",
      'Mixed': "Mixed",
      'Country_Districts': "Country Districts"
    };
    return labels[category] || category?.replace(/_/g, ' ') || 'Open';
  };

  const getStatusBadge = () => {
    // Upcoming tournament
    if (tournament.is_upcoming && tournament.starts_in_days <= 30) {
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          isDarkMode
            ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400'
            : 'bg-fsgu-gold-100 text-fsgu-gold-700'
        }`}>
          Starts in {tournament.starts_in_days} days
        </span>
      );
    }
    // Completed with results
    if (tournament.status === 'completed' && tournament.has_results) {
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          isDarkMode
            ? 'bg-fsgu-blue-900/30 text-fsgu-blue-400'
            : 'bg-fsgu-blue-100 text-fsgu-blue-700'
        }`}>
          Results Available
        </span>
      );
    }
    // In progress
    if (tournament.status === 'in_progress') {
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          isDarkMode
            ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
            : 'bg-fsgu-green-100 text-fsgu-green-700'
        }`}>
          In Progress
        </span>
      );
    }
    return null;
  };

  return (
    <Link
      to={`/tournaments/${tournament.id}`}
      className={`group block rounded-xl border overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
        featured
          ? isDarkMode
            ? 'bg-gradient-to-br from-fsgu-green-900/20 to-fsgu-blue-900/20 border-fsgu-green-500/30'
            : 'bg-gradient-to-br from-fsgu-green-50 to-fsgu-blue-50 border-fsgu-green-300'
          : isDarkMode
          ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50'
          : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-green-500/50'
      }`}
    >
      {/* Image */}
      {tournament.poster_image_url ? (
        <div className="relative h-48 overflow-hidden">
          <img
            src={tournament.poster_image_url}
            alt={tournament.tournament_name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {/* Overlay badges */}
          <div className="absolute top-3 right-3 flex flex-col gap-2">
            {tournament.is_featured && (
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-fsgu-gray-900/80' : 'bg-white/80'
              } backdrop-blur-sm`}>
                <Star className="w-4 h-4 text-fsgu-gold-500" fill="currentColor" />
              </div>
            )}
            {tournament.is_championship && (
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-fsgu-gray-900/80' : 'bg-white/80'
              } backdrop-blur-sm`}>
                <Trophy className="w-4 h-4 text-fsgu-green-500" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={`h-48 flex items-center justify-center ${
          isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-100'
        }`}>
          <Trophy className={`w-12 h-12 ${
            isDarkMode ? 'text-fsgu-gray-600' : 'text-fsgu-silver-400'
          }`} />
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        {/* Status Badge */}
        <div className="mb-3 h-6">
          {getStatusBadge()}
        </div>

        {/* Title */}
        <h3 className={`text-xl font-bold mb-2 group-hover:text-fsgu-green-500 transition-colors ${
          isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
        }`}>
          {tournament.tournament_name}
        </h3>

        {/* Category */}
        <div className="mb-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            getCategoryColor(tournament.category)
          }`}>
            {formatCategoryLabel(tournament.category)}
          </span>
        </div>

        {/* Description */}
        {tournament.description && (
          <p className={`text-sm mb-4 line-clamp-2 ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            {tournament.description}
          </p>
        )}

        {/* Details Grid */}
        <div className="space-y-2">
          {/* Dates */}
          <div className={`flex items-center text-sm ${
            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
          }`}>
            <Calendar className={`w-4 h-4 mr-2 ${
              isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
            }`} />
            <span>
              {formatDate(tournament.start_date)}
              {tournament.start_date !== tournament.end_date && (
                <> - {formatDate(tournament.end_date)}</>
              )}
            </span>
          </div>

          {/* Venue */}
          <div className={`flex items-center text-sm ${
            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
          }`}>
            <MapPin className={`w-4 h-4 mr-2 ${
              isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-500'
            }`} />
            <span className="truncate">
              {tournament.venue_name}
              {tournament.venue_city && `, ${tournament.venue_city}`}
            </span>
          </div>
        </div>

        {/* View Details Button */}
        <div className={`mt-4 pt-4 border-t ${
          isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
        }`}>
          <span className={`text-sm font-medium group-hover:text-fsgu-green-500 transition-colors ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
          }`}>
            View Details →
          </span>
        </div>
      </div>
    </Link>
  );
};

export default TournamentCard;